// src/utils/jueves.ts

/**
 * El grupo juega los jueves, así que "el último jueves" es la unidad de tiempo
 * del producto. Vive acá porque lo necesitan tanto el panel (para la franja de
 * estado) como la tarjeta de eventos (para decidir si ofrecer crear uno).
 */
export const getLastThursday = () => {
  const today = new Date();
  const day = today.getDay();

  const THURSDAY = 4;

  // Si hoy es jueves → diferencia = 0
  // Si no → retrocedemos hasta el jueves anterior
  const diff = day >= THURSDAY ? day - THURSDAY : 7 - (THURSDAY - day);

  const lastThursday = new Date(today);
  lastThursday.setDate(today.getDate() - diff);

  return lastThursday;
};

/**
 * Compara dos fechas ignorando la hora.
 *
 * Se usa la fecha local (no UTC) a propósito: es la misma conversión con la que
 * el resto de la app muestra los eventos, así que "mismo día" significa acá lo
 * mismo que se ve en pantalla.
 */
export const esMismoDia = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Un evento tal como lo devuelve /eventos, en lo mínimo que hace falta acá. */
interface EventoDeFecha {
  fecha: string;
  cancelado?: boolean;
}

/**
 * Devuelve el evento del último jueves, o null.
 *
 * Si ese día tiene más de uno (por ejemplo partido y peña) prefiere el que no
 * esté cancelado: es el que describe lo que realmente pasó.
 */
export function buscarEventoDelJueves<T extends EventoDeFecha>(
  eventos: T[],
  ultimoJueves: Date = getLastThursday()
): T | null {
  const delDia = eventos.filter((evento) =>
    esMismoDia(new Date(evento.fecha), ultimoJueves)
  );
  if (delDia.length === 0) return null;
  return delDia.find((evento) => !evento.cancelado) ?? delDia[0];
}

/**
 * ¿El último jueves ya tiene un evento cargado?
 *
 * Cuenta también los cancelados: el admin ya pasó por ahí y volver a
 * proponerle crearlo sería ruido. Y cuenta cualquier tipo — si ese jueves se
 * cargó una peña, el día ya está resuelto.
 */
export const hayEventoElUltimoJueves = (
  eventos: EventoDeFecha[],
  ultimoJueves: Date = getLastThursday()
) => buscarEventoDelJueves(eventos, ultimoJueves) !== null;
